import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertActionCloseButton,
  AlertActionLink,
} from '@patternfly/react-core';
import SyncAltIcon from '@patternfly/react-icons/dist/esm/icons/cloud-download-alt-icon';
import { useTranslation } from 'react-i18next';
import useServiceWorkerUpdate from 'hooks/useServiceWorkerUpdate';
import { activateWaitingServiceWorkerAndReload } from 'services/serviceWorkerUpdate';
import { storesContext } from 'stores';

import './styles.scss';

const PENDO_IDS = {
  alert: 'sw-update-alert',
  reload: 'sw-update-reload',
  dismiss: 'sw-update-dismiss',
} as const;

const PENDO_EVENTS = {
  promptShown: 'sw_update_prompt_shown',
  dismissed: 'sw_update_dismissed',
  accepted: 'sw_update_accepted',
} as const;

const getTrackEventMetadata = () => ({
  release_version: process.env.RELEASE_VERSION_NUMBER ?? null,
});

const ServiceWorkerUpdateAlert = () => {
  const { t } = useTranslation();
  const { pendoStore } = useContext(storesContext);
  const isUpdateAvailable = useServiceWorkerUpdate();
  const [closed, setClosed] = useState(false);
  const hasTrackedPromptShownRef = useRef(false);

  useEffect(() => {
    if (!isUpdateAvailable || closed || hasTrackedPromptShownRef.current) {
      return;
    }

    hasTrackedPromptShownRef.current = true;
    pendoStore.sendTrackEvent(
      PENDO_EVENTS.promptShown,
      getTrackEventMetadata(),
    );
  }, [closed, isUpdateAvailable, pendoStore]);

  if (!isUpdateAvailable || closed) {
    return null;
  }

  const handleRefresh = () => {
    pendoStore.sendTrackEvent(PENDO_EVENTS.accepted, getTrackEventMetadata());
    activateWaitingServiceWorkerAndReload();
  };

  const handleClose = () => {
    pendoStore.sendTrackEvent(PENDO_EVENTS.dismissed, getTrackEventMetadata());
    setClosed(true);
  };

  return (
    <div
      className="service-worker-update-alert pf-v5-theme-dark"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-pendo-id={PENDO_IDS.alert}
    >
      <Alert
        variant="info"
        title={t('A newer version of RHLS is available.')}
        customIcon={<SyncAltIcon aria-hidden="true" />}
        actionClose={
          <AlertActionCloseButton
            onClose={handleClose}
            data-pendo-id={PENDO_IDS.dismiss}
          />
        }
        actionLinks={
          <AlertActionLink
            onClick={handleRefresh}
            data-pendo-id={PENDO_IDS.reload}
          >
            {t('Update and reload')}
          </AlertActionLink>
        }
      />
    </div>
  );
};

export default ServiceWorkerUpdateAlert;
